import { getAppConfig, isSandbox } from "@/config/app-config";
import { CPClient, CPException } from "@EspaceParkQC/sdk-services-legacy";
import { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import axios from "axios";
import defaults from "lodash/defaults";
import isPlainObject from "lodash/isPlainObject";

const CENTRAL_API_URL = getAppConfig().VITE_CENTRAL_API_URL as string;
const CLICKNPARK_API_URL = getAppConfig().VITE_CLICKNPARK_API_URL as string;

// Types for react query
// ========================================

export type Query<TRequest, TData, TError> = TRequest & { queryOptions?: QueryOptions<TData, TError> };
export type QueryOptions<TData, TError> = Omit<UseQueryOptions<TData, TError>, "queryFn" | "queryKey">;
export type QueryError = CPException | Error;
/* How to use: Query<OnBehalfRequest<YOUR_REQUEST_TYPE>, YOUR_RESPONSE_TYPE, QueryError> */
export type OnBehalfRequest<TRequest = {}> = TRequest & { onBehalf?: string };
/* How to use: Query<CancellableQuery<YOUR_REQUEST_TYPE>, YOUR_RESPONSE_TYPE, QueryError> */
export type CancellableQuery<TRequest, TData, TError> = TRequest & { signal?: AbortSignal; queryOptions?: QueryOptions<TData, TError> };
export type QueryResponse<TData> = UseQueryResult<TData, QueryError>;
/* When using cancellable queries, the response will have an additional function to cancel the request */
export type CancellableQueryResponse<TData> = QueryResponse<TData> & { cancelRequest: () => void };

// Default values for stale time
// ========================================

/* We use react-query internally inside components and within one page, we can have multiple queries for the same resource. We need to make sure the data is not refetched too many times when components from the same
page are using the exact same query key. */
export const defaultStaleTime = 1000 * 60 * 1; // 1 minute

export const clicknpark = new CPClient(CLICKNPARK_API_URL, {
  app: "call-center",
  debug: {
    enabled: false,
    loggerName: "SDK",
  },
});

export const setClicknparkServicesCipTokenHeader = (token: string): void => {
  clicknpark.setCipAuthToken(token);
};

export const removeClicknparkServicesCipTokenHeader = (): void => {
  clicknpark.removeCipAuthToken();
};

export const centralServerAxiosClient = axios.create({
  baseURL: CENTRAL_API_URL,
});

centralServerAxiosClient.interceptors.request.use((config) => {
  const fromRequest = isPlainObject(config.params) ? { ...config.params } : {};
  const params = defaults(fromRequest, {
    service: getAppConfig().VITE_CENTRAL_API_SERVICE ?? (isSandbox() ? "dev-api" : "api"),
  });
  return { ...config, params };
});

export const setCentralCipTokenHeader = (token: string): void => {
  centralServerAxiosClient.defaults.headers.common["x-auth-token"] = token;
};

export const removeCentralCipTokenHeader = (): void => {
  delete centralServerAxiosClient.defaults.headers.common["x-auth-token"];
};